import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { AppState, SessionStatusResponse } from '@/types';
import { authApi } from '@/services/api';
import toast from 'react-hot-toast';

interface AuthStore extends AppState {
  // Actions
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  checkPlatformSession: () => Promise<void>;
  checkBrokerStatus: () => Promise<void>;
  disconnectBroker: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  handleKiteCallback: (requestToken: string) => Promise<boolean>;
  validateToken: (accessToken: string) => Promise<boolean>;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        isAuthenticated: false,
        user: null,
        platformToken: null,

        // Shared Broker Session (Shared by all users)
        brokerActive: false,
        brokerSession: null,

        isLoading: false,
        error: null,

        // Actions
        login: async (email: string, password: string): Promise<boolean> => {
          set({ isLoading: true, error: null });
          try {
            const response = await authApi.login(email, password);

            // Save token to localStorage for the axios interceptor
            localStorage.setItem('platform_token', response.access_token);

            set({
              isAuthenticated: true,
              user: response.user,
              platformToken: response.access_token,
              error: null,
              isLoading: false
            });

            toast.success(`Welcome back, ${response.user.full_name}!`);

            // Immediately check if broker is active for the whole system
            await get().checkBrokerStatus();

            return true;
          } catch (error: any) {
            const message = error.response?.data?.detail || error.message || 'Login failed';
            set({ error: message, isLoading: false, isAuthenticated: false, user: null });
            toast.error(message);
            return false;
          }
        },

        logout: () => {
          // Fire-and-forget: record the logout in the activity log. The token
          // is captured and passed explicitly (it's removed from storage
          // before the request interceptor runs); the UI never waits on it.
          const token = localStorage.getItem('platform_token');
          if (token) {
            authApi.platformLogout(token).catch(() => { /* best-effort */ });
          }
          localStorage.removeItem('platform_token');
          set({
            isAuthenticated: false,
            user: null,
            platformToken: null,
            brokerActive: false,
            brokerSession: null,
            error: null
          });
          toast.success('Logged out from platform');
        },

        checkPlatformSession: async () => {
          const token = localStorage.getItem('platform_token');
          if (!token) {
            set({ isAuthenticated: false, user: null, platformToken: null });
            return;
          }

          try {
            // Validate token by fetching current user info
            const user = await authApi.getMe();
            set({
              isAuthenticated: true,
              user,
              platformToken: token,
              error: null
            });

            // Also check global broker status
            await get().checkBrokerStatus();
          } catch (error) {
            console.warn('Platform session invalid or expired');
            localStorage.removeItem('platform_token');
            set({ isAuthenticated: false, user: null, platformToken: null });
          }
        },

        checkBrokerStatus: async () => {
          try {
            const response: SessionStatusResponse = await authApi.getSessionStatus();
            if (response.has_active_session && response.session_id) {
              set({
                brokerActive: true,
                brokerSession: {
                  id: response.session_id,
                  user_id: response.user_id!,
                  user_name: response.user_name!,
                  broker: 'zerodha',
                  last_used: response.last_used!,
                  expires_at: response.expires_at!,
                  is_active: true,
                }
              });
            } else {
              // CRITICAL FIX: Forcefully clear broker session state
              // This ensures stale localStorage data is removed when backend reports no active session
              set({
                brokerActive: false,
                brokerSession: null
              });

              // Force persist middleware to sync immediately
              // Without this, stale data may remain in localStorage across page refreshes
              const currentState = get();
              if (currentState.brokerSession !== null || currentState.brokerActive !== false) {
                console.warn('Clearing stale broker session from localStorage');
              }
            }
          } catch (error) {
            // If backend is unreachable, clear broker session to be safe
            // Better to show "not connected" than show stale "connected" state
            console.error('Broker status check failed:', error);
            set({
              brokerActive: false,
              brokerSession: null
            });
          }
        },

        disconnectBroker: async () => {
          set({ isLoading: true });
          try {
            await authApi.disconnectBroker();
            set({
              brokerActive: false,
              brokerSession: null,
              isLoading: false
            });
            toast.success('Broker session disconnected globally');
          } catch (error: any) {
            const message = error.response?.data?.detail || error.message || 'Disconnect failed';
            set({ isLoading: false });
            toast.error(message);
          }
        },

        getAccessToken: async (): Promise<string | null> => {
          try {
            const response = await authApi.getAccessToken();
            return response.access_token;
          } catch (error: any) {
            const message = error.response?.data?.detail || error.message || 'Failed to fetch token';
            toast.error(message);
            return null;
          }
        },

        handleKiteCallback: async (requestToken: string): Promise<boolean> => {
          // Guard: Only allow if platform is authenticated
          if (!get().isAuthenticated) {
            toast.error('Please log in to the platform first');
            return false;
          }

          const processedKey = `kite_token_processed_${requestToken}`;
          if (localStorage.getItem(processedKey)) return true;

          // CRITICAL: Clear existing state BEFORE starting validation
          set({
            isLoading: true,
            error: null,
            brokerActive: false,
            brokerSession: null
          });

          try {
            const response = await authApi.kiteCallback(requestToken);

            if (response.status === 'success') {
              localStorage.setItem(processedKey, 'true');

              set({
                brokerActive: true,
                brokerSession: {
                  id: response.session_id!,
                  user_id: response.user_id!,
                  user_name: response.user_name!,
                  broker: response.broker || 'ZERODHA',
                  last_used: new Date().toISOString(),
                  expires_at: new Date(Date.now() + 86400000).toISOString(),
                  is_active: true,
                },
                error: null,
                isLoading: false
              });

              toast.success(`Broker connected successfully by ${response.user_name}`);
              return true;
            } else {
              toast.error(response.error || 'Failed to link broker account');
              return false;
            }
          } catch (error: any) {
            const errorMessage = error.response?.data?.detail || error.message || 'Account linking failed';
            set({ error: errorMessage, isLoading: false, brokerActive: false, brokerSession: null });
            toast.error(errorMessage);
            return false;
          } finally {
            set({ isLoading: false });
          }
        },

        validateToken: async (accessToken: string): Promise<boolean> => {
          // CRITICAL: Clear existing state BEFORE starting validation
          set({
            isLoading: true,
            error: null,
            brokerActive: false,
            brokerSession: null
          });

          try {
            const response = await authApi.validateToken(accessToken);

            if (response.status === 'success') {
              // Calculate approximate IST expiry (6 AM next day)
              const now = new Date();
              const expiresAt = new Date(now);
              expiresAt.setHours(6, 0, 0, 0);
              if (now.getHours() >= 6) {
                expiresAt.setDate(expiresAt.getDate() + 1);
              }

              set({
                brokerActive: true,
                brokerSession: {
                  id: response.session_id!,
                  user_id: response.user_id!,
                  user_name: response.user_name!,
                  broker: response.broker || 'ZERODHA',
                  last_used: new Date().toISOString(),
                  expires_at: expiresAt.toISOString(),
                  is_active: true,
                },
                error: null,
                isLoading: false
              });

              toast.success(`Broker connected successfully by ${response.user_name}`);
              return true;
            } else {
              set({ error: response.error || 'Token validation failed', isLoading: false, brokerActive: false, brokerSession: null });
              toast.error(response.error || 'Token validation failed');
              return false;
            }
          } catch (error: any) {
            const errorMessage = error.response?.data?.detail || error.message || 'Token validation failed';
            set({ error: errorMessage, isLoading: false, brokerActive: false, brokerSession: null });
            toast.error(errorMessage);
            return false;
          }
        },

        clearError: () => set({ error: null }),
        setLoading: (loading: boolean) => set({ isLoading: loading }),
      }),
      {
        name: 'auth-store',
        partialize: (state) => ({
          isAuthenticated: state.isAuthenticated,
          user: state.user,
          platformToken: state.platformToken,
          brokerActive: state.brokerActive,
          brokerSession: state.brokerSession
        }),
      }
    ),
    {
      name: 'auth-store',
    }
  )
);